import { useState, useCallback, useEffect } from 'react';

export interface HistorySnapshot {
  id: string;
  filePath: string;
  content: string;
  timestamp: number;
  trigger: 'user_edit' | 'ai_generation_start' | 'ai_generation_complete' | 'file_save' | 'file_delete' | 'manual';
  description: string;
  size: number;
}

export interface FileHistory {
  filePath: string;
  snapshots: HistorySnapshot[];
  lastModified: number;
}

export interface LocalHistoryState {
  histories: Map<string, FileHistory>;
  maxSnapshotsPerFile: number;
  maxHistoryAge: number; // in milliseconds
}

const STORAGE_KEY = 'local-history';
const DEFAULT_MAX_SNAPSHOTS = 50;
const DEFAULT_MAX_AGE = 30 * 24 * 60 * 60 * 1000; // 30 days

export function useLocalHistory() {
  const [state, setState] = useState<LocalHistoryState>({
    histories: new Map(),
    maxSnapshotsPerFile: DEFAULT_MAX_SNAPSHOTS,
    maxHistoryAge: DEFAULT_MAX_AGE
  });

  // Load history from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const data = JSON.parse(stored);
        const histories = new Map<string, FileHistory>();
        
        // Convert stored data back to Map
        if (data.histories) {
          Object.entries(data.histories).forEach(([filePath, history]) => {
            histories.set(filePath, history as FileHistory);
          });
        }
        
        setState(prev => ({
          ...prev,
          histories,
          maxSnapshotsPerFile: data.maxSnapshotsPerFile || DEFAULT_MAX_SNAPSHOTS,
          maxHistoryAge: data.maxHistoryAge || DEFAULT_MAX_AGE
        }));
      }
    } catch (error) {
      console.error('Failed to load local history:', error);
    }
  }, []);

  // Save history to localStorage whenever state changes
  useEffect(() => {
    try {
      const dataToStore = {
        histories: Object.fromEntries(state.histories),
        maxSnapshotsPerFile: state.maxSnapshotsPerFile,
        maxHistoryAge: state.maxHistoryAge
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Failed to save local history:', error);
    }
  }, [state]);

  // Create a snapshot of a file
  const createSnapshot = useCallback((
    filePath: string,
    content: string,
    trigger: HistorySnapshot['trigger'],
    description?: string
  ) => {
    const snapshot: HistorySnapshot = {
      id: `snapshot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      filePath,
      content,
      timestamp: Date.now(),
      trigger,
      description: description || getDefaultDescription(trigger),
      size: new Blob([content]).size
    };

    setState(prev => {
      const newHistories = new Map(prev.histories);
      const existingHistory = newHistories.get(filePath);
      
      if (existingHistory) {
        // Add new snapshot and maintain limits
        const updatedSnapshots = [...existingHistory.snapshots, snapshot]
          .sort((a, b) => b.timestamp - a.timestamp) // Most recent first
          .slice(0, prev.maxSnapshotsPerFile); // Limit number of snapshots
        
        newHistories.set(filePath, {
          ...existingHistory,
          snapshots: updatedSnapshots,
          lastModified: Date.now()
        });
      } else {
        // Create new history for this file
        newHistories.set(filePath, {
          filePath,
          snapshots: [snapshot],
          lastModified: Date.now()
        });
      }

      return {
        ...prev,
        histories: newHistories
      };
    });

    return snapshot.id;
  }, []);

  // Get history for a specific file
  const getFileHistory = useCallback((filePath: string): FileHistory | undefined => {
    return state.histories.get(filePath);
  }, [state.histories]);

  // Get all snapshots for a file
  const getFileSnapshots = useCallback((filePath: string): HistorySnapshot[] => {
    const history = state.histories.get(filePath);
    return history ? history.snapshots : [];
  }, [state.histories]);

  // Get a specific snapshot
  const getSnapshot = useCallback((filePath: string, snapshotId: string): HistorySnapshot | undefined => {
    const history = state.histories.get(filePath);
    return history?.snapshots.find(snapshot => snapshot.id === snapshotId);
  }, [state.histories]);

  // Delete a snapshot
  const deleteSnapshot = useCallback((filePath: string, snapshotId: string) => {
    setState(prev => {
      const newHistories = new Map(prev.histories);
      const history = newHistories.get(filePath);
      
      if (history) {
        const updatedSnapshots = history.snapshots.filter(snapshot => snapshot.id !== snapshotId);
        
        if (updatedSnapshots.length > 0) {
          newHistories.set(filePath, {
            ...history,
            snapshots: updatedSnapshots,
            lastModified: Date.now()
          });
        } else {
          // Remove file history if no snapshots left
          newHistories.delete(filePath);
        }
      }

      return {
        ...prev,
        histories: newHistories
      };
    });
  }, []);

  // Clear all history for a file
  const clearFileHistory = useCallback((filePath: string) => {
    setState(prev => {
      const newHistories = new Map(prev.histories);
      newHistories.delete(filePath);
      
      return {
        ...prev,
        histories: newHistories
      };
    });
  }, []);

  // Clear all history
  const clearAllHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      histories: new Map()
    }));
  }, []);

  // Clean up old snapshots based on age
  const cleanupOldSnapshots = useCallback(() => {
    const cutoffTime = Date.now() - state.maxHistoryAge;
    
    setState(prev => {
      const newHistories = new Map(prev.histories);
      
      for (const [filePath, history] of newHistories.entries()) {
        const validSnapshots = history.snapshots.filter(snapshot => snapshot.timestamp > cutoffTime);
        
        if (validSnapshots.length > 0) {
          newHistories.set(filePath, {
            ...history,
            snapshots: validSnapshots
          });
        } else {
          newHistories.delete(filePath);
        }
      }

      return {
        ...prev,
        histories: newHistories
      };
    });
  }, [state.maxHistoryAge]);

  // Get statistics about the history
  const getHistoryStats = useCallback(() => {
    let totalSnapshots = 0;
    let totalSize = 0;
    let oldestSnapshot = Date.now();
    let newestSnapshot = 0;

    for (const history of state.histories.values()) {
      totalSnapshots += history.snapshots.length;
      
      for (const snapshot of history.snapshots) {
        totalSize += snapshot.size;
        oldestSnapshot = Math.min(oldestSnapshot, snapshot.timestamp);
        newestSnapshot = Math.max(newestSnapshot, snapshot.timestamp);
      }
    }

    return {
      totalFiles: state.histories.size,
      totalSnapshots,
      totalSize,
      oldestSnapshot: oldestSnapshot === Date.now() ? null : oldestSnapshot,
      newestSnapshot: newestSnapshot === 0 ? null : newestSnapshot
    };
  }, [state.histories]);

  // Auto-cleanup on mount and periodically
  useEffect(() => {
    cleanupOldSnapshots();
    
    // Set up periodic cleanup (every hour)
    const interval = setInterval(cleanupOldSnapshots, 60 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [cleanupOldSnapshots]);

  return {
    createSnapshot,
    getFileHistory,
    getFileSnapshots,
    getSnapshot,
    deleteSnapshot,
    clearFileHistory,
    clearAllHistory,
    cleanupOldSnapshots,
    getHistoryStats,
    state
  };
}

// Helper function to get default description for triggers
function getDefaultDescription(trigger: HistorySnapshot['trigger']): string {
  switch (trigger) {
    case 'user_edit':
      return 'User edit';
    case 'ai_generation_start':
      return 'Before AI generation';
    case 'ai_generation_complete':
      return 'After AI generation';
    case 'file_save':
      return 'File saved';
    case 'file_delete':
      return 'Before file deletion';
    case 'manual':
      return 'Manual snapshot';
    default:
      return 'Unknown';
  }
}

// Utility function to format file size
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Utility function to format relative time
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days} day${days !== 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  return `${seconds} second${seconds !== 1 ? 's' : ''} ago`;
}
